import React, {
  useState,
  useEffect
} from "react";

import {
  useNavigate
} from "react-router-dom";

import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  IconButton,
  Paper,
  CircularProgress
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { useCart } from "../context/CartContext";

const Cart = () => {

  const navigate = useNavigate();

  const {
    cart,
    removeFromCart,
    updateQuantity,
    getTotalPrice,
    fetchCart
  } = useCart();

  const [loading, setLoading] =
    useState(true);

  const subtotal = Number(getTotalPrice() || 0);
  const estimatedWalletDeduction = Math.min(100, subtotal);
  const estimatedDelivery = Math.max(subtotal - estimatedWalletDeduction, 0) > 200 ? 0 : 40;
  const estimatedPayable = Math.max(subtotal + estimatedDelivery, 0);

  // =========================
  // LOAD CART
  // =========================
  useEffect(() => {

    const loadCart = async () => {

      try {

        await fetchCart();

      } catch (err) {

        console.error(err);

      } finally {

        setLoading(false);

      }

    };

    loadCart();

  }, []);

  // =========================
  // QUANTITY
  // =========================
  const handleQuantityChange = (
    productId,
    newQuantity
  ) => {

    if (newQuantity > 0) {

      updateQuantity(
        productId,
        newQuantity
      );

    }

  };

  // =========================
  // REMOVE
  // =========================
  const handleRemove = (
    productId
  ) => {

    removeFromCart(productId);

  };

  // =========================
  // CHECKOUT
  // =========================
  const handleCheckout = () => {

    const token =
      localStorage.getItem("token");

    if (!token) {

      navigate("/login");

      return;

    }

    navigate("/checkout");

  };

  // =========================
  // CONTINUE SHOPPING
  // =========================
  const handleContinueShopping =
    () => {

      navigate("/");

    };

  // =========================
  // PDP
  // =========================
  const goToProduct = (id) => {

    navigate(`/product/${id}`);

  };

  // =========================
  // LOADING
  // =========================
  if (loading) {

    return (

      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >

        <CircularProgress
          sx={{
            color: "#8B0000"
          }}
        />

      </Box>

    );

  }

  // =========================
  // EMPTY CART
  // =========================
  if (cart.length === 0) {

    return (

      <Box
        sx={{
          minHeight: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 2,
          background:
            "linear-gradient(135deg,#fff8f6 0%, #f7ece7 100%)"
        }}
      >

        <Paper
          elevation={0}
          sx={{
            textAlign: "center",
            p: 6,
            borderRadius: "32px",
            maxWidth: 500,
            width: "100%",
            border:
              "1px solid rgba(139,0,0,0.08)",
            boxShadow:
              "0 20px 50px rgba(139,0,0,0.08)"
          }}
        >

          <ShoppingBagIcon
            sx={{
              fontSize: 90,
              color: "#8B0000",
              mb: 3
            }}
          />

          <Typography
            variant="h4"
            fontWeight={800}
            mb={2}
            color="#5c0000"
          >
            Your Cart is Empty
          </Typography>

          <Typography
            color="text.secondary"
            mb={4}
            sx={{
              lineHeight: 1.8
            }}
          >
            Looks like you haven’t added
            anything to your cart yet.
          </Typography>

          <Button
            variant="contained"
            onClick={
              handleContinueShopping
            }
            sx={{
              py: 1.8,
              px: 4,
              borderRadius: "18px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: 16,
              background:
                "linear-gradient(135deg,#5c0000,#8B0000)"
            }}
          >
            Explore Products
          </Button>

        </Paper>

      </Box>

    );

  }

  return (

    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg,#fff8f6 0%, #f7ece7 100%)",
        py: 5,
        px: {
          xs: 2,
          md: 5
        }
      }}
    >

      {/* HEADER */}
      <Box mb={5}>

        <Typography
          variant="h3"
          fontWeight={800}
          color="#5c0000"
          mb={1}
        >
          Shopping Cart
        </Typography>

        <Typography
          color="text.secondary"
        >
          Review your selected products
          before checkout
        </Typography>

      </Box>

      <Grid container spacing={4}>

        {/* CART ITEMS */}
        <Grid item xs={12} md={8}>

          <Box
            display="flex"
            flexDirection="column"
            gap={3}
          >

            {cart.map((item) => (

              <Paper
                key={item._id}
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "28px",
                  border:
                    "1px solid rgba(139,0,0,0.08)",
                  boxShadow:
                    "0 12px 35px rgba(139,0,0,0.06)"
                }}
              >

                <Grid
                  container
                  spacing={3}
                  alignItems="center"
                >

                  {/* IMAGE */}
                  <Grid
                    item
                    xs={12}
                    sm={3}
                  >

                    <Box
                      onClick={() =>
                        goToProduct(
                          item._id
                        )
                      }
                      sx={{
                        cursor: "pointer",
                        borderRadius:
                          "22px",
                        overflow:
                          "hidden",
                        background:
                          "#fff8f6",
                        border:
                          "1px solid rgba(139,0,0,0.08)",
                        p: 2,
                        height: 180,
                        display:
                          "flex",
                        alignItems:
                          "center",
                        justifyContent:
                          "center"
                      }}
                    >

                      <img
                        src={
                          item.image
                        }
                        alt={item.name}
                        style={{
                          maxWidth:
                            "100%",
                          maxHeight:
                            "100%",
                          objectFit:
                            "contain"
                        }}
                      />

                    </Box>

                  </Grid>

                  {/* CONTENT */}
                  <Grid
                    item
                    xs={12}
                    sm={9}
                  >

                    <Box>

                      <Typography
                        variant="h5"
                        fontWeight={700}
                        mb={1}
                        sx={{
                          cursor:
                            "pointer",
                          color:
                            "#2b2b2b"
                        }}
                        onClick={() =>
                          goToProduct(
                            item._id
                          )
                        }
                      >
                        {item.name}
                      </Typography>

                      <Typography
                        sx={{
                          color:
                            "#8B0000",
                          fontWeight: 700,
                          fontSize: 28,
                          mb: 3
                        }}
                      >
                        ₹
                        {Number(
                          item.price
                        ).toFixed(2)}
                      </Typography>

                      {/* QUANTITY */}
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems={{
                          xs: "flex-start",
                          md: "center"
                        }}
                        flexDirection={{
                          xs: "column",
                          md: "row"
                        }}
                        gap={3}
                      >

                        <Box
                          display="flex"
                          alignItems="center"
                          gap={2}
                        >

                          <IconButton
                            onClick={() =>
                              handleQuantityChange(
                                item._id,
                                item.quantity -
                                  1
                              )
                            }
                            disabled={
                              item.quantity <=
                              1
                            }
                            sx={{
                              width: 50,
                              height: 50,
                              background:
                                "#fff8f6",
                              border:
                                "1px solid rgba(139,0,0,0.08)"
                            }}
                          >
                            <RemoveIcon />
                          </IconButton>

                          <Typography
                            sx={{
                              minWidth: 40,
                              textAlign:
                                "center",
                              fontWeight: 700,
                              fontSize: 22
                            }}
                          >
                            {
                              item.quantity
                            }
                          </Typography>

                          <IconButton
                            onClick={() =>
                              handleQuantityChange(
                                item._id,
                                item.quantity +
                                  1
                              )
                            }
                            sx={{
                              width: 50,
                              height: 50,
                              background:
                                "#fff8f6",
                              border:
                                "1px solid rgba(139,0,0,0.08)"
                            }}
                          >
                            <AddIcon />
                          </IconButton>

                        </Box>

                        {/* ACTIONS */}
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={3}
                        >

                          <Typography
                            variant="h5"
                            fontWeight={800}
                            color="#5c0000"
                          >
                            ₹
                            {(
                              item.price *
                              item.quantity
                            ).toFixed(2)}
                          </Typography>

                          <IconButton
                            onClick={() =>
                              handleRemove(
                                item._id
                              )
                            }
                            sx={{
                              width: 52,
                              height: 52,
                              background:
                                "#fde8e8",
                              color:
                                "#b91c1c",
                              "&:hover":
                                {
                                  background:
                                    "#fecaca"
                                }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>

                        </Box>

                      </Box>

                    </Box>

                  </Grid>

                </Grid>

              </Paper>

            ))}

            {/* CONTINUE SHOPPING */}
            <Button
              onClick={
                handleContinueShopping
              }
              startIcon={
                <ArrowForwardIcon />
              }
              sx={{
                alignSelf:
                  "flex-start",
                color: "#5c0000",
                fontWeight: 700,
                textTransform: "none",
                fontSize: 15
              }}
            >
              Continue Shopping
            </Button>

          </Box>

        </Grid>

        {/* SUMMARY */}
        <Grid item xs={12} md={4}>

          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: "30px",
              position: "sticky",
              top: 100,
              border:
                "1px solid rgba(139,0,0,0.08)",
              boxShadow:
                "0 15px 40px rgba(139,0,0,0.08)"
            }}
          >

            <Typography
              variant="h5"
              fontWeight={800}
              mb={3}
              color="#5c0000"
            >
              Order Summary
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <Box
              display="flex"
              justifyContent="space-between"
              mb={2}
            >

              <Typography>Items Subtotal</Typography>

              <Typography
                fontWeight={700}
              >
                ₹{subtotal.toFixed(2)}
              </Typography>

            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              mb={2}
            >

              <Typography>
                Delivery
              </Typography>

              <Typography
                fontWeight={700}
                color="#16a34a"
              >
                {estimatedDelivery === 0 ? "FREE" : `₹${estimatedDelivery.toFixed(2)}`}
              </Typography>

            </Box>

            <Divider sx={{ my: 3 }} />

            <Box
              display="flex"
              justifyContent="space-between"
              mb={4}
            >

              <Typography
                variant="h6"
                fontWeight={800}
              >
                Total
              </Typography>

              <Typography
                variant="h5"
                fontWeight={800}
                color="#5c0000"
              >
                ₹
                {estimatedPayable.toFixed(2)}
              </Typography>

            </Box>

            <Button
              fullWidth
              variant="contained"
              onClick={handleCheckout}
              sx={{
                py: 2,
                borderRadius: "18px",
                fontSize: 16,
                fontWeight: 700,
                textTransform: "none",
                background:
                  "linear-gradient(135deg,#5c0000,#8B0000)",
                boxShadow:
                  "0 12px 30px rgba(139,0,0,0.22)"
              }}
            >
              Proceed to Checkout
            </Button>

          </Paper>

        </Grid>

      </Grid>

    </Box>

  );

};

export default Cart;